import { Loader } from "lucide-react";
import React, { useState } from "react";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  recordId?: string | number;
  useDirectApi?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  recordId,
  useDirectApi = false,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (!recordId) {
      console.error("No record ID provided for deletion");
      setError("No record ID provided for deletion");
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const apiUrl = useDirectApi
        ? `https://galaxy-alpr-be.onrender.com/api/plates/${recordId}`
        : `https://galaxy-alpr-be.onrender.com/detections/${recordId}`;

      console.log(`Deleting record with ID ${recordId} from API endpoint: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `Failed to delete: ${response.statusText}`);
      }

      console.log(`Successfully deleted record with ID ${recordId}`);

      // Call the onConfirm callback to notify parent component
      onConfirm();

      // Close the modal
      onClose();
    } catch (err) {
      console.error("Error deleting record:", err);
      setError(err instanceof Error ? err.message : "Failed to delete record");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl animate-pop-in relative">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{message}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={deleting}
          >
            {cancelText}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 disabled:bg-red-400"
          >
            {deleting && <Loader size={16} className="animate-spin" />}
            {confirmText}
          </button>
        </div>
        <style>
          {`
            @keyframes popIn {
              0% { opacity: 0; transform: scale(0.8);}
              100% { opacity: 1; transform: scale(1);}
            }
            .animate-pop-in {
              animation: popIn 0.2s cubic-bezier(0.4,0,0.2,1);
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;